import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "pwaInstallPromptStatus";

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isInStandaloneMode() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("other");

  useEffect(() => {
    if (isInStandaloneMode() || localStorage.getItem(STORAGE_KEY)) return;

    function handleBeforeInstallPrompt(event) {
      event.preventDefault();
      setDeferredPrompt(event);
      setPlatform("android");
      setOpen(true);
    }

    function handleAppInstalled() {
      localStorage.setItem(STORAGE_KEY, "installed");
      setOpen(false);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // beforeinstallprompt never fires on iOS Safari, so show manual instructions instead
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => {
        setPlatform("ios");
        setOpen(true);
      }, 2000);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "dismissed");
    setOpen(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    localStorage.setItem(
      STORAGE_KEY,
      outcome === "accepted" ? "installed" : "dismissed"
    );
    setDeferredPrompt(null);
    setOpen(false);
  }, [deferredPrompt]);

  return { open, platform, install, dismiss };
}
